import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMainStore } from "@/stores";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { showError } from "@/shared/utils/toast";
import { Check, X } from "lucide-react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { getTargetRoute } from "@/shared/utils/helperFunctions";

const GoogleCallbackHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useMainStore();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const processCallback = () => {
      try {
        setIsProcessing(true);
        const allParams = {};
        searchParams.forEach((value, key) => {
          allParams[key] = value;
        });
        const success = searchParams.get("success");
        const token = searchParams.get("token");
        const onboardFlag = searchParams.get("onboardFlag");

        const userId = searchParams.get("user[id]");
        const userName = searchParams.get("user[name]");
        const userEmail = searchParams.get("user[email]");
        const userRole = searchParams.get("user[role]") || "user";
        const userOrganizationId =
          searchParams.get("user[organization_id]") || null;
        const planParam =
          searchParams.get("user[plan]") || searchParams.get("plan");
        const trialEndsAtParam =
          searchParams.get("user[trial_ends_at]") ||
          searchParams.get("trial_ends_at");
        const trialApprovedAtParam =
          searchParams.get("user[trial_approved_at]") ||
          searchParams.get("trial_approved_at");
        const approvalStatusParam =
          searchParams.get("user[approval_status]") ||
          searchParams.get("approval_status");
        const organization_llm_type =
          searchParams.get("user[organization_llm_type]") ||
          searchParams.get("organization_llm_type") ||
          allParams["organization_llm_type"] ||
          allParams["user[organization_llm_type]"] ||
          undefined;
        const errorParam = searchParams.get("error");
        const errorMessage = searchParams.get("message");

        if (errorParam || success === "false") {
          throw new Error(errorMessage || "Authentication failed");
        }

        if (!token) {
          throw new Error("No authentication token received");
        }

        if (!userId || !userEmail) {
          throw new Error("Incomplete user data received");
        }

        localStorage.setItem("token", token);
        window.dispatchEvent(new Event("tokenUpdated"));

        const isOnboarded = onboardFlag === "true";
        const approvalStatusResolved =
          approvalStatusParam && approvalStatusParam !== ""
            ? approvalStatusParam
            : !isOnboarded
              ? null
              : "approved";
        const userForRoute = {
          id: userId,
          name: userName || userEmail,
          email: userEmail,
          organization_id: userOrganizationId,
          profileImage: null,
          isOnboarded,
          role: userRole,
          approvalStatus: approvalStatusResolved,
          hasAccessRequest: false,
          ...(!isOnboarded ? { pricingPageCompleted: false } : {}),
          ...(planParam && { plan: planParam }),
          ...(trialApprovedAtParam && {
            trial_approved_at: trialApprovedAtParam,
          }),
          ...(trialEndsAtParam && { trial_ends_at: trialEndsAtParam }),
          ...(organization_llm_type && {
            organization_llm_type: organization_llm_type,
          }),
        };

        setUser(userForRoute);
        setIsSuccess(true);
        navigate(getTargetRoute(userForRoute), { replace: true });
      } catch (err) {
        console.error("Error processing Google callback:", err);
        setError(err.message || "Authentication failed. Please try again.");
        showError(err.message || "Authentication failed");
        navigate("/signup", { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, setUser]);

  if (isSuccess) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="text-success-500 w-8 h-8" />
          </div>
          <div className="text-success-500 text-lg font-semibold">
            Authentication Successful!
          </div>
          <div className="text-gray-700">
            Welcome! Redirecting to your dashboard...
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4">
          <Loader text="Authenticating with Google..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="text-error-500 w-8 h-8" />
          </div>
          <div className="text-red-600 text-lg font-semibold">
            Authentication Error
          </div>
          <div className="text-gray-700">{error}</div>
          <PrimaryButton onClick={() => navigate("/signup")}>
            Return to Sign Up
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleCallbackHandler;
